import React, { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import {
  Plus,
  Edit,
  Eye,
  UserPlus,
  Filter,
  Download,
  Upload,
  X,
  User,
  BookOpen,
  Users,
  Trash2,
  AlertTriangle,
  Award,
} from "lucide-react";
import {
  getStudents,
  deleteStudent,
  bulkDeleteStudents,
  downloadAdmissionLetter,
  getAvailableAdmissionLetters,
} from "../../services/students";
import { getDepartments } from "../../services/departments";
import { getCourses } from "../../services/courses";
import {
  offerScholarship,
  getStudentScholarship,
} from "../../services/scholarships";
import { verifyPassword } from "../../services/auth";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import StudentModal from "../../components/forms/StudentModal";
import Pagination from "../../components/common/Pagination";
import { useHorizontalDragScroll } from "../../hooks/useHorizontalDragScroll";

const SecretaryStudents: React.FC = () => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const [searchTerm, setSearchTerm] = useState("");
  const [activeSearchTerm, setActiveSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [departmentFilter, setDepartmentFilter] = useState<string>("all");
  const [courseFilter, setCourseFilter] = useState<string>("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudentId, setEditingStudentId] = useState<string | null>(null);
  const [viewingStudent, setViewingStudent] = useState<any | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deletingStudent, setDeletingStudent] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isDownloadingAdmissionLetter, setIsDownloadingAdmissionLetter] =
    useState(false);
  const [availableLetters, setAvailableLetters] = useState<any[]>([]);
  const [showDiscountModal, setShowDiscountModal] = useState(false);
  const [discountStudent, setDiscountStudent] = useState<any | null>(null);
  const [discountAmount, setDiscountAmount] = useState("");
  const [discountReason, setDiscountReason] = useState("");

  const queryClient = useQueryClient();

  // Handle Enter key press to trigger search
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      setActiveSearchTerm(searchTerm);
      setCurrentPage(1);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearchTerm, filterStatus, departmentFilter, courseFilter]);

  // Fetch students
  const {
    data: studentsResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "students",
      activeSearchTerm,
      filterStatus,
      departmentFilter,
      courseFilter,
      currentPage,
      pageSize,
    ],
    queryFn: () =>
      getStudents({
        search: activeSearchTerm.trim() || undefined,
        status: filterStatus === "all" ? undefined : filterStatus,
        departmentId: departmentFilter === "all" ? undefined : departmentFilter,
        courseId: courseFilter === "all" ? undefined : courseFilter,
        page: currentPage,
        limit: pageSize,
      }),
    refetchOnWindowFocus: false,
    enabled: true,
  });

  // Fetch departments for filter dropdown
  const { data: departmentsResponse } = useQuery({
    queryKey: ["departments"],
    queryFn: () => getDepartments(1, 100, undefined, undefined, true),
  });

  // Fetch courses for filter dropdown
  const { data: coursesResponse } = useQuery({
    queryKey: ["courses-lookup"],
    queryFn: () => getCourses({ limit: 1000 }), // Get all courses
  });

  // Fetch total student statistics (unfiltered)
  const { data: statsResponse, isLoading: isStatsLoading } = useQuery({
    queryKey: ["student-stats"],
    queryFn: () => getStudents({ limit: 1000 }), // Get a large number to calculate stats
    refetchOnWindowFocus: false,
    enabled: true,
  });

  const students = studentsResponse?.data || [];
  const pagination = studentsResponse?.pagination;
  const allStudents = statsResponse?.data || [];

  const handleCreateStudent = () => {
    setEditingStudentId(null);
    setIsModalOpen(true);
  };

  const handleEditStudent = (studentId: string) => {
    setEditingStudentId(studentId);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingStudentId(null);
    // Refresh the students list and stats
    queryClient.invalidateQueries({ queryKey: ["students"] });
    queryClient.invalidateQueries({ queryKey: ["student-stats"] });
  };

  const handleViewStudent = (student: any) => {
    setViewingStudent(student);
    // Fetch available admission letters
    fetchAvailableLetters(student._id);
  };

  const handleCloseViewModal = () => {
    setViewingStudent(null);
    setAvailableLetters([]);
  };

  const fetchAvailableLetters = async (studentId: string) => {
    try {
      const response = await getAvailableAdmissionLetters(studentId);
      if (response.success && response.data) {
        setAvailableLetters(response.data.availableLetters || []);
      }
    } catch (error) {
      console.error("Error fetching available admission letters:", error);
      // Don't show error to user, just log it
    }
  };

  const handleDownloadAdmissionLetterClick = async (
    letter: any
  ) => {
    if (!viewingStudent) return;

    try {
      setIsDownloadingAdmissionLetter(true);
      
      const options: any = {};
      if (letter.courseName) options.course = letter.courseName;
      if (letter.language) options.language = letter.language.toLowerCase();

      const blobUrl = await downloadAdmissionLetter(
        viewingStudent._id,
        options,
      );

      // Create a temporary link and trigger download
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = letter.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Revoke the blob URL after download
      window.URL.revokeObjectURL(blobUrl);

      toast.success("Admission letter downloaded successfully");
    } catch (error) {
      console.error("Download admission letter error:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to download admission letter",
      );
    } finally {
      setIsDownloadingAdmissionLetter(false);
    }
  };

  const handleSelectStudent = (studentId: string) => {
    setSelectedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([]);
    } else {
      setSelectedStudents(filteredStudents.map((s: any) => s._id));
    }
  };

  const handleDeleteClick = (studentId?: string) => {
    if (studentId) {
      setDeletingStudent(studentId);
    } else {
      setDeletingStudent(null);
    }
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!deletePassword) {
      toast.error("Please enter your password to confirm deletion");
      return;
    }

    setIsDeleting(true);

    try {
      // Verify password first
      const verifyResult = await verifyPassword(deletePassword);
      if (!verifyResult.success) {
        toast.error("Invalid password. Please try again.");
        setIsDeleting(false);
        return;
      }

      // Perform deletion
      if (deletingStudent) {
        // Single delete
        const result = await deleteStudent(deletingStudent);
        if (result.success) {
          toast.success("Student deleted successfully");
          queryClient.invalidateQueries({ queryKey: ["students"] });
          queryClient.invalidateQueries({ queryKey: ["student-stats"] });
        } else {
          toast.error(result.message || "Failed to delete student");
        }
      } else if (selectedStudents.length > 0) {
        // Bulk delete
        const result = await bulkDeleteStudents(selectedStudents);
        if (result.success) {
          toast.success(
            `${selectedStudents.length} student(s) deleted successfully`,
          );
          setSelectedStudents([]);
          queryClient.invalidateQueries({ queryKey: ["students"] });
          queryClient.invalidateQueries({ queryKey: ["student-stats"] });
        } else {
          toast.error(result.message || "Failed to delete students");
        }
      }

      // Close modal and reset
      setShowDeleteModal(false);
      setDeletePassword("");
      setDeletingStudent(null);
    } catch (error) {
      toast.error("An error occurred during deletion");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setDeletePassword("");
    setDeletingStudent(null);
  };

  const handleDiscountClick = (student: any) => {
    setDiscountStudent(student);
    setDiscountAmount("");
    setDiscountReason("");
    setShowDiscountModal(true);
  };

  const offerScholarshipMutation = useMutation({
    mutationFn: async (data: {
      studentId: string;
      percentage: number;
      reason?: string;
    }) => {
      return await offerScholarship(data);
    },
    onSuccess: () => {
      toast.success("Discount/Scholarship offered successfully");
      queryClient.invalidateQueries({ queryKey: ["students"] });
      queryClient.invalidateQueries({ queryKey: ["student-stats"] });
      setShowDiscountModal(false);
      setDiscountStudent(null);
      setDiscountAmount("");
      setDiscountReason("");
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to offer discount");
    },
  });

  const handleSubmitDiscount = () => {
    if (!discountStudent || !discountAmount) {
      toast.error("Please enter a valid discount amount");
      return;
    }

    const amount = parseFloat(discountAmount);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Discount amount must be greater than 0");
      return;
    }

    // Calculate per-period fees from enrolled courses
    const courses =
      discountStudent.coursesInfo || discountStudent.courses || [];
    const perPeriodFees = courses.reduce((sum: number, course: any) => {
      if (typeof course === "object") {
        const perPeriod =
          course.feeStructure?.perPeriodAmount ||
          course.fees?.perPeriodAmount ||
          course.fees?.installmentAmount ||
          (course.fees?.totalFee && course.fees?.numberOfPeriods
            ? course.fees.totalFee / course.fees.numberOfPeriods
            : 0);
        return sum + (perPeriod || 0);
      }
      return sum;
    }, 0);

    if (perPeriodFees <= 0) {
      toast.error(
        "Cannot apply discount: Student has no per-period fee structure assigned",
      );
      return;
    }

    if (amount > perPeriodFees) {
      toast.error(
        `Discount amount cannot exceed per-period fees (KSh ${Math.round(perPeriodFees).toLocaleString()})`,
      );
      return;
    }

    // Calculate percentage from per-period amount
    const percentage = (amount / perPeriodFees) * 100;

    offerScholarshipMutation.mutate({
      studentId: discountStudent._id,
      percentage: Math.round(percentage * 100) / 100, // Round to 2 decimal places
      reason:
        discountReason ||
        `Discount of KSh ${amount.toLocaleString()} offered by secretary`,
    });
  };

  // Use server-filtered students directly, no need for additional client-side filtering
  const filteredStudents = useMemo(() => {
    // The search filtering is already done on the server side
    // Only apply any additional client-side filters if needed
    return students;
  }, [students]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading students...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 min-h-screen">
        <div className="bg-gradient-to-br from-red-50 to-pink-50/80 border border-red-200/60 rounded-2xl p-6 shadow-lg">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <X className="w-8 h-8 text-white" />
          </div>
          <p className="text-red-600 font-semibold text-center text-lg">
            Error loading students. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">
                Student Registration
              </h1>
              <p className="text-slate-600 mt-1 font-medium">
                Register new students and manage existing student records
              </p>
            </div>
            <div className="flex gap-3">
              {selectedStudents.length > 0 && (
                <button
                  onClick={() => handleDeleteClick()}
                  className="bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 text-white px-6 py-3 rounded-xl flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold"
                >
                  <Trash2 className="w-5 h-5" />
                  Delete ({selectedStudents.length})
                </button>
              )}
              <button
                onClick={handleCreateStudent}
                className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-6 py-3 rounded-xl flex items-center gap-2 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 font-semibold"
              >
                <UserPlus className="w-5 h-5" />
                Register New Student
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl p-6 mb-6 shadow-lg border border-slate-200/60">
          <div className="flex flex-col gap-4">
            {/* Top row: Search and action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
              <div className="flex-1 max-w-2xl relative">
                <input
                  type="text"
                  placeholder="Search students by name, email, or student ID... (Press Enter to search)"
                  className="w-full pl-10 pr-16 py-3 border border-slate-300/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-300 font-medium text-slate-900"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                />

                {/* Clear search button */}
                {searchTerm && (
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setActiveSearchTerm("");
                    }}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-lg hover:bg-slate-100"
                    title="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`px-4 py-3 border-2 rounded-xl font-bold shadow-sm hover:shadow-lg transition-all duration-300 flex items-center gap-2 ${
                    showFilters ||
                    filterStatus !== "all" ||
                    departmentFilter !== "all" ||
                    courseFilter !== "all"
                      ? "bg-blue-50 border-blue-300 text-blue-700"
                      : "bg-white border-slate-200 text-slate-700"
                  }`}
                >
                  {showFilters ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Filter className="h-5 w-5" />
                  )}
                  <span className="hidden sm:inline">
                    {showFilters ? "Close" : "Filters"}
                  </span>
                  {(filterStatus !== "all" ||
                    departmentFilter !== "all" ||
                    courseFilter !== "all") &&
                    !showFilters && (
                      <span className="bg-blue-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {
                          [
                            filterStatus !== "all",
                            departmentFilter !== "all",
                            courseFilter !== "all",
                          ].filter(Boolean).length
                        }
                      </span>
                    )}
                </button>
              </div>
            </div>

            {/* Expandable filters section */}
            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 pt-3 border-t border-slate-200 animate-slide-down">
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-300 font-semibold text-slate-700"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="graduated">Graduated</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Department
                  </label>
                  <select
                    value={departmentFilter}
                    onChange={(e) => setDepartmentFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-300 font-semibold text-slate-700"
                  >
                    <option value="all">All Departments</option>
                    {departmentsResponse?.data?.map((dept: any) => (
                      <option key={dept._id} value={dept._id}>
                        {dept.name} ({dept.code})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1">
                    Course
                  </label>
                  <select
                    value={courseFilter}
                    onChange={(e) => setCourseFilter(e.target.value)}
                    className="w-full px-4 py-3 border border-slate-300/60 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white shadow-sm transition-all duration-300 font-semibold text-slate-700"
                  >
                    <option value="all">All Courses</option>
                    {coursesResponse?.data?.map((course: any) => (
                      <option key={course._id} value={course._id}>
                        {course.name} ({course.level})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2 lg:col-span-3 flex justify-end">
                  <button
                    onClick={() => {
                      setFilterStatus("all");
                      setDepartmentFilter("all");
                      setCourseFilter("all");
                    }}
                    className="text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors"
                  >
                    Clear all filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Search Results Info */}
        {activeSearchTerm && (
          <div className="mb-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100/80 border border-blue-200/60 rounded-xl px-4 py-3 shadow-sm">
              <p className="text-sm text-blue-700 font-semibold">
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    Searching for "{activeSearchTerm}"...
                  </span>
                ) : (
                  <span>
                    Found {filteredStudents.length} student(s) matching "
                    {activeSearchTerm}"
                    {filteredStudents.length === 0 && (
                      <span className="ml-2">
                        - Try adjusting your search terms
                      </span>
                    )}
                  </span>
                )}
              </p>
            </div>
          </div>
        )}

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">
                  Total Students
                </p>
                <p className="text-2xl font-bold text-slate-900">
                  {allStudents.length}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-300 to-blue-400 flex items-center justify-center shadow-md">
                <UserPlus className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">
                  Active Students
                </p>
                <p className="text-2xl font-bold text-emerald-600">
                  {
                    allStudents.filter((s) => s.academicStatus === "active")
                      .length
                  }
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-md">
                <UserPlus className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">
                  New This Month
                </p>
                <p className="text-2xl font-bold text-purple-600">
                  {
                    allStudents.filter((s) => {
                      const created = new Date(
                        s.createdAt || s.enrollmentDate || new Date(),
                      );
                      const now = new Date();
                      return (
                        created.getMonth() === now.getMonth() &&
                        created.getFullYear() === now.getFullYear()
                      );
                    }).length
                  }
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center shadow-md">
                <Plus className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>

          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg p-6 border border-slate-200/60 hover:shadow-xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-600">
                  Inactive Students
                </p>
                <p className="text-2xl font-bold text-amber-600">
                  {
                    allStudents.filter((s) => s.academicStatus !== "active")
                      .length
                  }
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-300 to-yellow-400 flex items-center justify-center shadow-md">
                <Eye className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Students Table */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 overflow-hidden">
          <div className="p-6 border-b border-slate-200/60 bg-gradient-to-r from-slate-50 to-blue-50">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">
                Student Records
              </h2>
              <div className="flex gap-3">
                <button className="px-4 py-2 bg-gradient-to-br from-emerald-50 to-emerald-100/80 hover:from-emerald-100 hover:to-emerald-200/80 text-emerald-700 rounded-xl transition-all duration-300 flex items-center gap-2 border border-emerald-200/60 shadow-sm hover:shadow-md font-semibold">
                  <Upload className="w-4 h-4" />
                  Import
                </button>
                <button className="px-4 py-2 bg-gradient-to-br from-blue-50 to-blue-100/80 hover:from-blue-100 hover:to-blue-200/80 text-blue-700 rounded-xl transition-all duration-300 flex items-center gap-2 border border-blue-200/60 shadow-sm hover:shadow-md font-semibold">
                  <Download className="w-4 h-4" />
                  Export
                </button>
              </div>
            </div>
          </div>

          <div ref={tableScrollRef} className="overflow-x-auto">
            <table className="w-full min-w-[550px]">
              {/* Ensures minimum width for proper display */}
              <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <input
                      type="checkbox"
                      checked={
                        selectedStudents.length === filteredStudents.length &&
                        filteredStudents.length > 0
                      }
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Student Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Student ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Class
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Department
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {filteredStudents.map((student, index) => (
                  <tr
                    key={student._id}
                    className={`${
                      index % 2 === 0 ? "bg-white" : "bg-slate-50/30"
                    } hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50/50 transition-all duration-300`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student._id)}
                        onChange={() => handleSelectStudent(student._id)}
                        className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        {/* Profile Picture */}
                        <div className="flex-shrink-0">
                          {student.photoUrl ||
                          student.userInfo?.profileDetails?.profilePicture ? (
                            <img
                              src={
                                student.photoUrl ||
                                student.userInfo?.profileDetails?.profilePicture
                              }
                              alt={`${student.userInfo?.firstName} ${student.userInfo?.lastName}`}
                              className="w-10 h-10 rounded-full object-cover border-2 border-slate-200 shadow-sm"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = "none";
                                const parent = target.parentElement;
                                if (parent) {
                                  const fallback =
                                    document.createElement("div");
                                  fallback.className =
                                    "w-10 h-10 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-sm";
                                  fallback.innerHTML =
                                    '<svg class="w-5 h-5 text-slate-600" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"></path></svg>';
                                  parent.appendChild(fallback);
                                }
                              }}
                            />
                          ) : (
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-300 to-slate-400 flex items-center justify-center shadow-sm">
                              <User className="w-5 h-5 text-slate-600" />
                            </div>
                          )}
                        </div>
                        {/* Name and Admission Number */}
                        <div>
                          <div className="text-sm font-bold text-slate-900">
                            {student.userInfo?.firstName}{" "}
                            {student.userInfo?.lastName}
                          </div>
                          <div className="text-xs text-slate-500 font-medium">
                            ADM: {student.admissionNumber || "N/A"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 font-medium">
                      {student.studentId || "N/A"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 font-medium">
                      {student.classInfo?.name || "Not assigned"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 font-medium">
                      {student.departmentInfo?.name || "Not assigned"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-3 py-1 text-xs font-bold rounded-full shadow-sm ${
                          student.academicStatus === "active"
                            ? "bg-gradient-to-r from-emerald-400 to-emerald-500 text-white"
                            : student.academicStatus === "inactive"
                              ? "bg-gradient-to-r from-yellow-400 to-yellow-500 text-white"
                              : student.academicStatus === "suspended"
                                ? "bg-gradient-to-r from-red-400 to-pink-500 text-white"
                                : student.academicStatus === "graduated"
                                  ? "bg-gradient-to-r from-blue-400 to-blue-500 text-white"
                                  : "bg-gradient-to-r from-slate-300 to-slate-400 text-white"
                        }`}
                      >
                        {student.academicStatus
                          ? student.academicStatus.charAt(0).toUpperCase() +
                            student.academicStatus.slice(1)
                          : "Unknown"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditStudent(student._id)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded-xl hover:bg-blue-50 transition-all duration-300 shadow-sm hover:shadow-md"
                          title="Edit Student"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleViewStudent(student)}
                          className="text-emerald-600 hover:text-emerald-900 p-2 rounded-xl hover:bg-emerald-50 transition-all duration-300 shadow-sm hover:shadow-md"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDiscountClick(student)}
                          className="text-purple-600 hover:text-purple-900 p-2 rounded-xl hover:bg-purple-50 transition-all duration-300 shadow-sm hover:shadow-md"
                          title="Offer Discount/Scholarship"
                        >
                          <Award className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteClick(student._id)}
                          className="text-red-600 hover:text-red-900 p-2 rounded-xl hover:bg-red-50 transition-all duration-300 shadow-sm hover:shadow-md"
                          title="Delete Student"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredStudents.length === 0 && (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center mx-auto mb-4 shadow-md">
                <UserPlus className="w-8 h-8 text-slate-500" />
              </div>
              <h3 className="text-lg font-bold text-slate-900 mb-2">
                No students found
              </h3>
              <p className="text-slate-600 font-medium">
                {searchTerm
                  ? "Try adjusting your search criteria"
                  : "Get started by registering your first student"}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && students.length > 0 && (
          <div className="mt-6 bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-4">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              onPageChange={setCurrentPage}
              pageSize={pageSize}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        )}
      </div>
      {isModalOpen && (
        <StudentModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          studentId={editingStudentId || undefined}
        />
      )}

      {/* View Student Details Modal */}
      {viewingStudent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div
              className="fixed inset-0 bg-slate-900/75 backdrop-blur-sm transition-opacity"
              onClick={handleCloseViewModal}
            ></div>

            {/* Center the modal */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">
              &#8203;
            </span>

            {/* Modal panel */}
            <div className="relative inline-block align-bottom bg-white/95 backdrop-blur-xl rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 z-10 border border-slate-200/60">
              {/* Header */}
              <div className="flex justify-between items-center mb-6 pb-4 border-b border-slate-200/60">
                <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-300 to-emerald-400 flex items-center justify-center shadow-md">
                    <Eye className="h-5 w-5 text-white" />
                  </div>
                  Student Details
                </h3>
                <div className="flex items-center gap-2">
                  {/* Show admission letter dropdown if letters are available */}
                  {availableLetters.length > 0 && (
                    <div className="relative">
                      {availableLetters.length === 1 ? (
                        // Single letter - direct download button
                        <button
                          onClick={() => handleDownloadAdmissionLetterClick(availableLetters[0])}
                          disabled={isDownloadingAdmissionLetter}
                          className="px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-sm rounded-xl transition-all duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl font-semibold"
                          title="Download Admission Letter"
                        >
                          {isDownloadingAdmissionLetter ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                              <span>Downloading...</span>
                            </>
                          ) : (
                            <>
                              <Download className="h-4 w-4" />
                              <span>Admission Letter</span>
                            </>
                          )}
                        </button>
                      ) : (
                        // Multiple letters - dropdown menu
                        <div className="group relative">
                          <button
                            className="px-3 py-2 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-sm rounded-xl transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-xl font-semibold"
                            title="Download Admission Letters"
                          >
                            <Download className="h-4 w-4" />
                            <span>Admission Letters ({availableLetters.length})</span>
                          </button>
                          {/* Dropdown menu */}
                          <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                            <div className="px-4 py-2 text-xs font-semibold text-slate-500 border-b border-slate-200">
                              Select letter to download:
                            </div>
                            {availableLetters.map((letter: any, index: number) => (
                              <button
                                key={index}
                                onClick={() => handleDownloadAdmissionLetterClick(letter)}
                                disabled={isDownloadingAdmissionLetter}
                                className="w-full px-4 py-3 text-left hover:bg-emerald-50 transition-colors flex items-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Download className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-semibold text-slate-900 truncate">
                                    {letter.courseName}
                                  </div>
                                  {letter.language && (
                                    <div className="text-xs text-slate-500">
                                      {letter.language}
                                    </div>
                                  )}
                                </div>
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <button
                    onClick={handleCloseViewModal}
                    className="text-slate-400 hover:text-slate-600 transition-colors p-2 rounded-xl hover:bg-slate-100"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Student Details Content */}
              <div className="space-y-6">
                {/* Personal Information */}
                <div className="border border-slate-200/60 rounded-xl p-5 bg-gradient-to-br from-slate-50 to-blue-50/30 shadow-sm">
                  <h4 className="text-md font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-blue-300 to-blue-400 flex items-center justify-center shadow-md">
                      <User className="h-4 w-4 text-white" />
                    </div>
                    Personal Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Full Name
                      </label>
                      <p className="text-sm text-slate-900 font-medium">
                        {viewingStudent.userInfo?.firstName}{" "}
                        {viewingStudent.userInfo?.lastName}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Email
                      </label>
                      <p className="text-sm text-slate-900 font-medium">
                        {viewingStudent.userInfo?.email}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Phone
                      </label>
                      <p className="text-sm text-slate-900 font-medium">
                        {viewingStudent.userInfo?.profileDetails?.phone ||
                          "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Gender
                      </label>
                      <p className="text-sm text-slate-900 font-medium">
                        {viewingStudent.userInfo?.profileDetails?.gender ||
                          "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Date of Birth
                      </label>
                      <p className="text-sm text-slate-900 font-medium">
                        {viewingStudent.userInfo?.profileDetails?.dateOfBirth
                          ? new Date(
                              viewingStudent.userInfo.profileDetails
                                .dateOfBirth,
                            ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Address
                      </label>
                      <p className="text-sm text-slate-900 font-medium">
                        {viewingStudent.userInfo?.profileDetails?.address
                          ?.street
                          ? `${viewingStudent.userInfo.profileDetails.address.street}, ${viewingStudent.userInfo.profileDetails.address.city}, ${viewingStudent.userInfo.profileDetails.address.state}`
                          : "N/A"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Academic Information */}
                <div className="border border-slate-200/60 rounded-xl p-5 bg-gradient-to-br from-slate-50 to-blue-50/30 shadow-sm">
                  <h4 className="text-md font-bold text-slate-900 mb-4 flex items-center gap-2">
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-300 to-purple-400 flex items-center justify-center shadow-md">
                      <BookOpen className="h-4 w-4 text-white" />
                    </div>
                    Academic Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Student ID
                      </label>
                      <p className="text-sm text-slate-900 font-medium">
                        {viewingStudent.studentId}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Admission Number
                      </label>
                      <p className="text-sm text-slate-900 font-medium">
                        {viewingStudent.admissionNumber || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Current Class
                      </label>
                      <p className="text-sm text-slate-900 font-medium">
                        {viewingStudent.classInfo
                          ? `${viewingStudent.classInfo.name} - ${viewingStudent.classInfo.grade} (Section ${viewingStudent.classInfo.section})`
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Academic Status
                      </label>
                      <span
                        className={`inline-flex px-3 py-1 text-xs font-bold rounded-full shadow-sm ${
                          viewingStudent.academicStatus === "active"
                            ? "bg-gradient-to-r from-emerald-400 to-emerald-500 text-white"
                            : viewingStudent.academicStatus === "inactive"
                              ? "bg-gradient-to-r from-yellow-400 to-yellow-500 text-white"
                              : viewingStudent.academicStatus === "suspended"
                                ? "bg-gradient-to-r from-red-400 to-pink-500 text-white"
                                : viewingStudent.academicStatus === "graduated"
                                  ? "bg-gradient-to-r from-blue-400 to-blue-500 text-white"
                                  : "bg-gradient-to-r from-slate-300 to-slate-400 text-white"
                        }`}
                      >
                        {viewingStudent.academicStatus
                          ? viewingStudent.academicStatus
                              .charAt(0)
                              .toUpperCase() +
                            viewingStudent.academicStatus.slice(1)
                          : "Unknown"}
                      </span>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Enrollment Date
                      </label>
                      <p className="text-sm text-slate-900 font-medium">
                        {viewingStudent.enrollmentDate
                          ? new Date(
                              viewingStudent.enrollmentDate,
                            ).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-bold text-slate-600">
                        Courses/Subjects
                      </label>
                      <div className="text-sm text-slate-900 font-medium">
                        {(() => {
                          // Collect courses/subjects from multiple sources
                          const allSubjects = [];

                          // Get courses from the new coursesInfo field (populated courses)
                          if (
                            viewingStudent.coursesInfo &&
                            viewingStudent.coursesInfo.length > 0
                          ) {
                            allSubjects.push(...viewingStudent.coursesInfo);
                          }

                          // Get courses from the courses field (if not populated)
                          if (
                            viewingStudent.courses &&
                            viewingStudent.courses.length > 0 &&
                            (!viewingStudent.coursesInfo ||
                              viewingStudent.coursesInfo.length === 0)
                          ) {
                            allSubjects.push(...viewingStudent.courses);
                          }

                          // Get subjects from user profile
                          if (
                            viewingStudent.userInfo?.profileDetails?.subjects &&
                            viewingStudent.userInfo.profileDetails.subjects
                              .length > 0
                          ) {
                            allSubjects.push(
                              ...viewingStudent.userInfo.profileDetails
                                .subjects,
                            );
                          }

                          // Get subjects from class info
                          if (
                            viewingStudent.classInfo &&
                            viewingStudent.classInfo.subjects &&
                            viewingStudent.classInfo.subjects.length > 0
                          ) {
                            allSubjects.push(
                              ...viewingStudent.classInfo.subjects,
                            );
                          }

                          // Remove duplicates
                          const uniqueSubjects = allSubjects.filter(
                            (subject, index, self) =>
                              index ===
                              self.findIndex(
                                (s) =>
                                  (s._id && s._id === subject._id) ||
                                  (s.name && s.name === subject.name) ||
                                  (typeof s === "string" &&
                                    typeof subject === "string" &&
                                    s === subject),
                              ),
                          );

                          if (uniqueSubjects.length > 0) {
                            return (
                              <div className="space-y-2">
                                <div className="flex flex-wrap gap-1">
                                  {uniqueSubjects.map(
                                    (subject: any, index: number) => (
                                      <span
                                        key={index}
                                        className="inline-flex px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full"
                                      >
                                        {typeof subject === "string"
                                          ? subject
                                          : subject.name ||
                                            subject.code ||
                                            subject.title ||
                                            `Subject ${index + 1}`}
                                      </span>
                                    ),
                                  )}
                                </div>
                                {/* Show total course fees if available */}
                                {(() => {
                                  const totalCourseFees = uniqueSubjects.reduce(
                                    (sum: number, subject: any) => {
                                      if (
                                        typeof subject === "object" &&
                                        subject.fees &&
                                        subject.fees.totalFee
                                      ) {
                                        return sum + subject.fees.totalFee;
                                      }
                                      return sum;
                                    },
                                    0,
                                  );

                                  return totalCourseFees > 0 ? (
                                    <p className="text-xs text-gray-600 mt-1">
                                      Course Fees: KSh{" "}
                                      {totalCourseFees.toLocaleString()}
                                    </p>
                                  ) : null;
                                })()}
                              </div>
                            );
                          } else {
                            // Show class name as the main course since subjects list is empty
                            return (
                              <div className="space-y-2">
                                {viewingStudent.classInfo ? (
                                  <div>
                                    <span className="inline-flex px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                                      {viewingStudent.classInfo.name} (
                                      {viewingStudent.classInfo.grade})
                                    </span>
                                    <p className="text-xs text-gray-500 mt-1">
                                      Enrolled in this class. Individual courses
                                      may not be configured yet.
                                    </p>
                                  </div>
                                ) : (
                                  <p className="text-gray-500">
                                    No courses/subjects assigned
                                  </p>
                                )}
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Class Room
                      </label>
                      <p className="text-sm text-gray-900">
                        {viewingStudent.classInfo &&
                        viewingStudent.classInfo.room
                          ? `Room ${viewingStudent.classInfo.room.number}, ${viewingStudent.classInfo.room.building} (Floor ${viewingStudent.classInfo.room.floor})`
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Fee Status
                      </label>
                      <div className="text-sm text-gray-900">
                        {(() => {
                          // Get fee information from class first, then student
                          let feeInfo = null;
                          let classBasedFees = null;

                          // Try to get class-based fees
                          if (
                            viewingStudent.classInfo &&
                            viewingStudent.classInfo.fees
                          ) {
                            classBasedFees = viewingStudent.classInfo.fees;
                          }

                          // Use student fees if available, otherwise use class fees
                          if (
                            viewingStudent.fees &&
                            (viewingStudent.fees.totalFeeStructure > 0 ||
                              viewingStudent.fees.totalPaid > 0 ||
                              viewingStudent.fees.totalBalance > 0)
                          ) {
                            feeInfo = viewingStudent.fees;
                          } else if (classBasedFees) {
                            // Create a mock student fee structure based on class fees
                            feeInfo = {
                              totalFeeStructure:
                                classBasedFees.totalFee ||
                                classBasedFees.tuitionFee ||
                                0,
                              totalPaid: viewingStudent.fees?.totalPaid || 0,
                              totalBalance:
                                (classBasedFees.totalFee ||
                                  classBasedFees.tuitionFee ||
                                  0) - (viewingStudent.fees?.totalPaid || 0),
                              feeStatus:
                                viewingStudent.fees?.feeStatus ||
                                (viewingStudent.fees?.totalPaid > 0
                                  ? "partial"
                                  : "pending"),
                            };
                          }

                          return feeInfo ? (
                            <div className="space-y-1">
                              <p>
                                Total Fee: KSh{" "}
                                {feeInfo.totalFeeStructure?.toLocaleString() ||
                                  0}
                              </p>
                              <p>
                                Paid: KSh{" "}
                                {feeInfo.totalPaid?.toLocaleString() || 0}
                              </p>
                              <p>
                                Balance: KSh{" "}
                                {feeInfo.totalBalance?.toLocaleString() || 0}
                              </p>
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  feeInfo.feeStatus === "paid"
                                    ? "bg-green-100 text-green-800"
                                    : feeInfo.feeStatus === "partial"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-red-100 text-red-800"
                                }`}
                              >
                                {feeInfo.feeStatus?.charAt(0).toUpperCase() +
                                  feeInfo.feeStatus?.slice(1) || "Pending"}
                              </span>
                              {classBasedFees && (
                                <p className="text-xs text-gray-500 mt-1">
                                  Class Tuition: KSh{" "}
                                  {classBasedFees.tuitionFee?.toLocaleString() ||
                                    0}
                                  {classBasedFees.additionalFees &&
                                    classBasedFees.additionalFees.length > 0 &&
                                    ` + Additional Fees`}
                                </p>
                              )}
                            </div>
                          ) : (
                            "No fee information available"
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Parent/Guardian Information */}
                <div className="border border-gray-200 rounded-lg p-4">
                  <h4 className="text-md font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    Parent/Guardian Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Father's Name
                      </label>
                      <p className="text-sm text-gray-900">
                        {viewingStudent.parentGuardianInfo?.fatherName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Mother's Name
                      </label>
                      <p className="text-sm text-gray-900">
                        {viewingStudent.parentGuardianInfo?.motherName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Guardian Name
                      </label>
                      <p className="text-sm text-gray-900">
                        {viewingStudent.parentGuardianInfo?.guardianName ||
                          "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Guardian Phone
                      </label>
                      <p className="text-sm text-gray-900">
                        {viewingStudent.parentGuardianInfo?.guardianPhone ||
                          "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Guardian Email
                      </label>
                      <p className="text-sm text-gray-900">
                        {viewingStudent.parentGuardianInfo?.guardianEmail ||
                          "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">
                        Emergency Contact
                      </label>
                      <p className="text-sm text-gray-900">
                        {viewingStudent.parentGuardianInfo?.emergencyContact
                          ?.name || "N/A"}
                        {viewingStudent.parentGuardianInfo?.emergencyContact
                          ?.phone &&
                          ` - ${viewingStudent.parentGuardianInfo.emergencyContact.phone}`}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseViewModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discount/Scholarship Modal */}
      {showDiscountModal && discountStudent && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] flex flex-col overflow-hidden border border-slate-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-500 to-indigo-500 p-6 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Offer Discount/Scholarship
                  </h3>
                  <p className="text-purple-100 text-sm font-medium">
                    {discountStudent.userInfo?.firstName}{" "}
                    {discountStudent.userInfo?.lastName}
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <p className="text-sm text-purple-800 font-semibold">
                  💰 Apply a discount or scholarship to reduce the student's
                  fees
                </p>
                <p className="text-xs text-purple-600 mt-2">
                  The discount will be applied to the per-period amount for all
                  outstanding and future invoices.
                </p>
                {(() => {
                  // Calculate per-period fees from enrolled courses
                  const courses =
                    discountStudent.coursesInfo ||
                    discountStudent.courses ||
                    [];
                  const perPeriodFees = courses.reduce(
                    (sum: number, course: any) => {
                      if (typeof course === "object") {
                        // Prioritize feeStructure.perPeriodAmount, then fallback to fees
                        const perPeriod =
                          course.feeStructure?.perPeriodAmount ||
                          course.fees?.perPeriodAmount ||
                          course.fees?.installmentAmount ||
                          (course.fees?.totalFee && course.fees?.numberOfPeriods
                            ? course.fees.totalFee / course.fees.numberOfPeriods
                            : 0);
                        return sum + (perPeriod || 0);
                      }
                      return sum;
                    },
                    0,
                  );

                  if (perPeriodFees > 0 || courses.length > 0) {
                    return (
                      <div className="space-y-1">
                        <p className="text-xs text-purple-700 font-semibold">
                          Fee Structure (Per Period):
                        </p>
                        {courses.map((course: any, index: number) => {
                          if (typeof course === "object") {
                            const perPeriod =
                              course.feeStructure?.perPeriodAmount ||
                              course.fees?.perPeriodAmount ||
                              course.fees?.installmentAmount ||
                              (course.fees?.totalFee &&
                              course.fees?.numberOfPeriods
                                ? Math.round(
                                    course.fees.totalFee /
                                      course.fees.numberOfPeriods,
                                  )
                                : 0);
                            const courseName =
                              course.name ||
                              course.title ||
                              `Course ${index + 1}`;
                            return (
                              <p
                                key={index}
                                className="text-xs text-purple-600 pl-2"
                              >
                                • {courseName}: KSh{" "}
                                {(perPeriod || 0).toLocaleString()}/period
                              </p>
                            );
                          }
                          return null;
                        })}
                        {perPeriodFees > 0 && (
                          <p className="text-xs text-purple-700 font-bold pl-2">
                            Total Per Period: KSh{" "}
                            {Math.round(perPeriodFees).toLocaleString()}
                          </p>
                        )}
                      </div>
                    );
                  }
                  return null;
                })()}
                {/* Show existing discount/scholarship */}
                {discountStudent.scholarshipPercentage > 0 && (
                  <div className="mt-2 pt-2 border-t border-purple-300 bg-purple-100/50 rounded-lg p-2">
                    <p className="text-xs text-purple-900 font-bold flex items-center gap-1">
                      <Award className="w-3 h-3" />
                      Existing Discount: {discountStudent.scholarshipPercentage}
                      %
                      {(() => {
                        // Calculate based on per-period amount
                        const courses =
                          discountStudent.coursesInfo ||
                          discountStudent.courses ||
                          [];
                        const perPeriodFees = courses.reduce(
                          (sum: number, course: any) => {
                            if (typeof course === "object") {
                              const perPeriod =
                                course.feeStructure?.perPeriodAmount ||
                                course.fees?.perPeriodAmount ||
                                course.fees?.installmentAmount ||
                                (course.fees?.totalFee &&
                                course.fees?.numberOfPeriods
                                  ? course.fees.totalFee /
                                    course.fees.numberOfPeriods
                                  : 0);
                              return sum + (perPeriod || 0);
                            }
                            return sum;
                          },
                          0,
                        );
                        const existingDiscountAmount = perPeriodFees
                          ? Math.round(
                              (perPeriodFees *
                                discountStudent.scholarshipPercentage) /
                                100,
                            )
                          : 0;
                        return existingDiscountAmount > 0
                          ? ` (KSh ${existingDiscountAmount.toLocaleString()}/period)`
                          : "";
                      })()}
                    </p>
                    {discountStudent.scholarshipReason && (
                      <p className="text-xs text-purple-700 mt-1">
                        Reason: {discountStudent.scholarshipReason}
                      </p>
                    )}
                    <p className="text-xs text-orange-600 mt-1 font-semibold">
                      ⚠️ Applying a new discount will replace the existing one
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Discount Amount per Period (KSh){" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max={
                    (() => {
                      const courses =
                        discountStudent.coursesInfo ||
                        discountStudent.courses ||
                        [];
                      return courses.reduce((sum: number, course: any) => {
                        if (typeof course === "object") {
                          const perPeriod =
                            course.feeStructure?.perPeriodAmount ||
                            course.fees?.perPeriodAmount ||
                            course.fees?.installmentAmount ||
                            (course.fees?.totalFee &&
                            course.fees?.numberOfPeriods
                              ? course.fees.totalFee /
                                course.fees.numberOfPeriods
                              : 0);
                          return sum + (perPeriod || 0);
                        }
                        return sum;
                      }, 0);
                    })() || undefined
                  }
                  step="0.01"
                  value={discountAmount}
                  onChange={(e) => setDiscountAmount(e.target.value)}
                  placeholder="Enter amount per period in KSh"
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                  disabled={offerScholarshipMutation.isPending}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {(() => {
                    const courses =
                      discountStudent.coursesInfo ||
                      discountStudent.courses ||
                      [];
                    const perPeriodFees = courses.reduce(
                      (sum: number, course: any) => {
                        if (typeof course === "object") {
                          const perPeriod =
                            course.feeStructure?.perPeriodAmount ||
                            course.fees?.perPeriodAmount ||
                            course.fees?.installmentAmount ||
                            (course.fees?.totalFee &&
                            course.fees?.numberOfPeriods
                              ? course.fees.totalFee /
                                course.fees.numberOfPeriods
                              : 0);
                          return sum + (perPeriod || 0);
                        }
                        return sum;
                      },
                      0,
                    );
                    return perPeriodFees > 0
                      ? `Enter discount amount per period (max: KSh ${Math.round(perPeriodFees).toLocaleString()})`
                      : "Enter the discount amount per period in KSh";
                  })()}
                </p>
                {discountAmount &&
                  parseFloat(discountAmount) > 0 &&
                  (() => {
                    const courses =
                      discountStudent.coursesInfo ||
                      discountStudent.courses ||
                      [];
                    const perPeriodFees = courses.reduce(
                      (sum: number, course: any) => {
                        if (typeof course === "object") {
                          const perPeriod =
                            course.feeStructure?.perPeriodAmount ||
                            course.fees?.perPeriodAmount ||
                            course.fees?.installmentAmount ||
                            (course.fees?.totalFee &&
                            course.fees?.numberOfPeriods
                              ? course.fees.totalFee /
                                course.fees.numberOfPeriods
                              : 0);
                          return sum + (perPeriod || 0);
                        }
                        return sum;
                      },
                      0,
                    );
                    return perPeriodFees > 0 ? (
                      <p className="text-xs text-purple-600 mt-2 font-semibold">
                        ✓ This equals{" "}
                        {(
                          (parseFloat(discountAmount) / perPeriodFees) *
                          100
                        ).toFixed(2)}
                        % discount per period
                      </p>
                    ) : null;
                  })()}
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Reason (Optional)
                </label>
                <textarea
                  value={discountReason}
                  onChange={(e) => setDiscountReason(e.target.value)}
                  placeholder="e.g., Merit scholarship, Financial hardship, Sibling discount"
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all resize-none"
                  disabled={offerScholarshipMutation.isPending}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Provide a reason for this discount (optional)
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-6 py-4 flex gap-3 justify-end border-t border-slate-200 flex-shrink-0">
              <button
                onClick={() => {
                  setShowDiscountModal(false);
                  setDiscountStudent(null);
                  setDiscountAmount("");
                  setDiscountReason("");
                }}
                disabled={offerScholarshipMutation.isPending}
                className="px-6 py-2.5 text-sm font-bold text-slate-700 bg-white border-2 border-slate-300 rounded-xl hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitDiscount}
                disabled={offerScholarshipMutation.isPending || !discountAmount}
                className="px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-purple-500 to-indigo-500 rounded-xl hover:from-purple-600 hover:to-indigo-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl"
              >
                {offerScholarshipMutation.isPending ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Applying...
                  </>
                ) : (
                  <>
                    <Award className="w-4 h-4" />
                    Apply Discount
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-pink-500 p-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Confirm Deletion
                  </h3>
                  <p className="text-red-100 text-sm font-medium">
                    This action cannot be undone
                  </p>
                </div>
              </div>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-800 font-semibold">
                  {deletingStudent
                    ? "You are about to delete 1 student record."
                    : `You are about to delete ${selectedStudents.length} student records.`}
                </p>
                <p className="text-xs text-red-600 mt-2">
                  All associated data including fees, attendance, and grades
                  will be permanently removed.
                </p>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 mb-2">
                  Enter your password to confirm
                </label>
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Your password"
                  className="w-full px-4 py-3 border-2 border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-all"
                  disabled={isDeleting}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isDeleting) {
                      handleConfirmDelete();
                    }
                  }}
                />
              </div>
            </div>

            {/* Footer */}
            <div className="bg-slate-50 px-6 py-4 flex gap-3 justify-end">
              <button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                className="px-6 py-2.5 text-sm font-bold text-slate-700 bg-white border-2 border-slate-300 rounded-xl hover:bg-slate-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={isDeleting || !deletePassword}
                className="px-6 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-red-500 to-pink-500 rounded-xl hover:from-red-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></div>
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Delete{" "}
                    {deletingStudent
                      ? "Student"
                      : `${selectedStudents.length} Students`}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SecretaryStudents;
